import React, { createContext, useContext, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import usePWA from '../hooks/usePWA';
import useNetworkStatus from '../hooks/useNetworkStatus';
import useOfflineQueue from '../hooks/useOfflineQueue';
import useBadge from '../hooks/useBadge';
import axios from 'axios';

const PWAContext = createContext(null);

/**
 * PWA Context Provider
 * Provides PWA hooks and offline queue functionality to the entire app
 */
export function PWAProvider({ children }) {
  const pwa = usePWA();
  const network = useNetworkStatus();
  const queryClient = useQueryClient();

  const offlineQueue = useOfflineQueue({
    onSync: async (item) => {
      try {
        // Sync queued API calls when back online
        const { url, method = 'POST', data, headers } = item;
        await axios({
          method,
          url,
          data,
          headers,
        });
        return { success: true };
      } catch (error) {
        console.error('[PWA] Failed to sync queued item:', error);
        throw error;
      }
    },
    onError: (error) => {
      console.error('[PWA] Offline queue error:', error);
    },
  });

  // Update badge with queue count
  useBadge(offlineQueue.queueSize);

  const updateBlock = useMemo(() => {
    const mutationCache = queryClient?.getMutationCache?.();
    const mutations = mutationCache?.getAll?.() || [];
    const isPending = (key) =>
      mutations.some((m) => {
        const mk = m?.options?.mutationKey;
        if (!mk) return false;
        const asString = Array.isArray(mk) ? mk.join(':') : String(mk);
        return asString === key && m.state?.status === 'pending';
      });

    const vmtSendInProgress = isPending('vmtSend');
    const buySellCreateInProgress = isPending('buySellCreate');
    const buySellUpdateInProgress = isPending('buySellUpdate');

    const buySellDafUploadInProgress = buySellCreateInProgress || buySellUpdateInProgress;

    let reason = null;
    if (vmtSendInProgress) reason = 'VMT send in progress';
    else if (buySellDafUploadInProgress) reason = 'Buy/Sell upload in progress';

    return {
      vmtSendInProgress,
      buySellDafUploadInProgress,
      updateBlocked: Boolean(reason),
      updateBlockedReason: reason,
    };
  }, [queryClient]);

  const value = {
    ...pwa,
    network,
    offlineQueue,
    updateBlock,
  };

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
}

/**
 * Hook to access PWA context
 */
export function usePWAContext() {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWAContext must be used within PWAProvider');
  }
  return context;
}

// Optional access for debug-only components that can render outside the provider.
export function useOptionalPWAContext() {
  return useContext(PWAContext);
}

/**
 * Hook for offline-aware API calls
 * Automatically queues mutations when offline
 */
export function useOfflineMutation(mutationFn, options = {}) {
  const { offlineQueue, isOffline } = usePWAContext();
  const { onMutate, onSuccess, onError, ...restOptions } = options;

  const enhancedMutationFn = async (variables) => {
    if (isOffline) {
      // Queue for later sync
      const queued = offlineQueue.addToQueue({
        url: variables?.url || '/api/unknown',
        method: variables?.method || 'POST',
        data: variables,
        headers: variables?.headers || {},
      });

      if (queued.queued) {
        throw new Error('Request queued for offline sync');
      }
    }

    return mutationFn(variables);
  };

  return {
    mutationFn: enhancedMutationFn,
    onMutate: async (variables) => {
      if (onMutate) return onMutate(variables);
    },
    onSuccess: async (data, variables, context) => {
      if (onSuccess) return onSuccess(data, variables, context);
    },
    onError: async (error, variables, context) => {
      if (error.message === 'Request queued for offline sync') {
        // Don't treat queued requests as errors
        return;
      }
      if (onError) return onError(error, variables, context);
    },
    ...restOptions,
  };
}

